import React, { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Megaphone, BellRinging, BellSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pushSupported, subscribeToPush, unsubscribeFromPush, getPushStatus } from "@/lib/push";
import { formatApiError } from "@/api";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [pushStatus, setPushStatus] = useState({ supported: false });

  useEffect(() => { api.get("/announcements").then((r) => setItems(r.data)); }, []);
  useEffect(() => { getPushStatus().then(setPushStatus); }, [user]);

  const enablePush = async () => {
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled");
      setPushStatus(await getPushStatus());
    } catch (err) { toast.error(err.message || formatApiError(err)); }
  };
  const disablePush = async () => {
    try {
      await unsubscribeFromPush();
      toast.success("Push notifications disabled");
      setPushStatus(await getPushStatus());
    } catch (err) { toast.error(err.message || formatApiError(err)); }
  };

  return (
    <div data-testid="announcements-page">
      <div className="mb-4">
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1">Latest</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Announcements</h1>
      </div>

      {pushSupported() && user && (
        <div className="mb-5 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center justify-between gap-3" data-testid="push-settings">
          <div className="flex items-start gap-3">
            {pushStatus.subscribed ? <BellRinging size={20} className="text-[#000000] mt-0.5" /> : <BellSlash size={20} className="text-[#7A828C] mt-0.5" />}
            <div>
              <div className="font-medium text-sm">Push notifications</div>
              <div className="text-xs text-[#7A828C]">{pushStatus.subscribed ? "You'll get live alerts from organisers." : "Get room swaps and welcome reminders."}</div>
            </div>
          </div>
          {pushStatus.subscribed ? (
            <Button variant="outline" size="sm" onClick={disablePush} className="rounded-sm" data-testid="push-disable-btn">Disable</Button>
          ) : (
            <Button size="sm" onClick={enablePush} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="push-enable-btn">Enable</Button>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#7A828C]">
          <Megaphone size={32} className="mx-auto mb-2 opacity-50" />
          No announcements yet. Organisers will post updates here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`announcement-${a.id}`} className="p-4 bg-[#FFFFFF] border-l-4 border-l-[#000000] border-t border-r border-b border-[#E5E9F0] rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">{new Date(a.sent_at).toLocaleString()}</span>
              </div>
              <h3 className="font-display text-lg mb-1">{a.title}</h3>
              <p className="text-sm text-[#5a5a5a] whitespace-pre-wrap">{a.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
